import { Button, Form, FormInstance, Input, message, Select, Space, Tabs, Typography } from "antd";
import React, { useEffect, useMemo, useRef } from "react";
import SkillRateCardTable from "./Steps/RateCards";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import BenchDevelopers from "../Leads/Developers/BenchDevelopers";
import InfiniteScrollList from "../../components/Forms/ListView/InfiniteScrollList";
import { getVendorData } from "../../actions/auth";
import { formatPriceIndian } from "../../constants/functions";
import Badge from "../../components/Badge";
import { MasterConfigs } from "../../interfaces/entity";
import { constants } from "../../constants/common";
import moment from "moment";
import FormSetup from "../../components/Forms/FormSetup";
import RenderForm from "../../components/Forms/Form";

const AddDevelopers = () => {
    const [userData, setUserData] = React.useState<any>(null);
    const [addDeveloper, setAddDeveloper] = React.useState(false);
    const [vendor, setVendor] = React.useState<string>('');
    const [form, setForm] = React.useState<FormInstance<any> | null>(null);
    const dispatch = useDispatch();

    const developerConfig: MasterConfigs = useMemo(() => ({
        api: () => '/bench-developers',
        setForm: (formInstance) => setForm(formInstance),
        getFormColumns: () => vendor ? [
            {
                label: 'First Name',
                name: 'firstName',
                type: 'text',
                inputProps: {
                    addonBefore: <Form.Item name="preName">
                        <Select style={{ width: 100 }} placeholder="Title">
                            {constants.PRENAME.map(ele => <Select.Option key={ele.value} value={ele.value}>{ele.name}</Select.Option>)}
                        </Select>
                    </Form.Item>
                },
                rules: [{ required: true, message: 'First Name is required' }],
            },
            {
                label: 'Last Name',
                name: 'lastName',
                type: 'text',
            },
            {
                label: 'Email',
                name: 'email',
                type: 'text',
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            return emailRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid email address'));
                        },
                    },
                ],
            },
            {
                label: 'Mobile',
                name: 'mobile',
                type: 'text',
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const mobileRegex = /^[0-9]{10,15}$/;
                            return mobileRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid mobile number'));
                        },
                    },
                ],
            },
            {
                label: 'Experience',
                name: 'experience',
                type: 'text',
                inputProps: {
                    addonAfter: 'Years',
                },
                render: (text) => {
                    return <span>{text || 'N/A'} Years</span>;
                },
                rules: [{
                    validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        const experienceRegex = /^[0-9]{1,3}$/;
                        return experienceRegex.test(value)
                            ? Promise.resolve()
                            : Promise.reject(new Error('Enter a valid experience'));
                    },
                }],
            },
            {
                label: 'Developer Availability',
                name: 'developerAvailability',
                type: 'select',
                selectProps: {
                    showSearch: true,
                    optionFilterProp: 'children',
                    allowClear: true,
                },
                children: constants.DEVELOPER_AVAILABILITY,
                render: (text) => {
                    return constants.DEVELOPER_AVAILABILITY.find(ele => ele.value === text)?.name || 'N/A';
                },
            },
            {
                label: 'Notice Period',
                name: 'noticePeriodDays',
                type: 'text',
                inputProps: {
                    addonAfter: 'Days',
                },
                render: (text) => {
                    return <span>{text || 'N/A'} Days</span>;
                },
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const noticePeriodRegex = /^[0-9]{1, 3}$/;
                            return noticePeriodRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid notice period'));
                        },
                    },
                ]
            },
            {
                label: 'Last Working Date',
                name: 'lastWorkingDate',
                type: 'date',
                dateProps: {
                    format: 'DD/MM/YYYY',
                },
                render: (text) => {
                    return text ? moment(new Date(text)).format('DD/MM/YYYY') : 'N/A';
                },
            },
            {
                label: 'Budget',
                name: 'vendorBudget',
                type: 'text',
                inputProps: {
                    prefix: 'INR ',
                },
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const rangeRegex = /^[0-9]+(\.[0-9]{1, 2})?$/;
                            return rangeRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid vendor billing rate'));
                        },
                    },
                ],
                render: (text) => {
                    return text ? `INR ${text}` : 'N/A';
                },
            },
            {
                label: 'City',
                name: ['address', 'city'],
                render: (text) => text || 'N/A',
                type: 'text',
            },
            {
                label: 'State',
                name: ['address', 'state'],
                render: (text) => text || 'N/A',
                type: 'text',
            },
            {
                label: 'CV',
                name: 'cv',
                type: 'file',
                uploadProps: {
                    accept: ".pdf,.doc,.docx",
                    maxCount: 1,
                    beforeUpload: (file) => {
                        const isValid = file.size / 1024 / 1024 < 5; // Limit file size to 5MB
                        if (!isValid) {
                            message.error('File must be smaller than 5MB!');
                        }
                        return isValid;
                    },
                    listType: 'text',
                },
                rules: [
                    {
                        required: true,
                        message: 'Please upload the CV',
                    },
                ],
            }
        ] : [{
            label: 'Your Email',
            name: 'vendor',
            type: 'text',
            inputProps: {
                placeholder: 'Enter your registered vendor email'
            },
            rules: [{ required: true, message: 'Vendor email is required' }],
        }],
        combo: [],
    }), [vendor]);

    return (
        <>
            <div className="min-h-screen bg-[#e5e8f3]">
                <div className="bg-white w-full fixed top-0 z-10 shadow-md">
                    <img src={require('./../../assets/logo/logo.png')} alt="Logo" className="w-50 p-4" />
                </div>
                <div className="flex justify-center mt-14">
                    <div className="container mx-auto p-4 w-full max-w-5xl bg-white rounded mt-10 mb-10">
                        <div className="mb-4">
                            <h2 className="text-lg font-semibold">Share developer profile</h2>
                            <span className="text-sm text-gray-500">Please fill in the details below</span>
                        </div>
                        <div className="p-4">
                            <RenderForm config={developerConfig} formInstance={(f) => setForm(f)} />
                            <Button onClick={() => form?.submit()}>Save</Button>
                        </div>
                    </div>
                </div>
            </div>
        </>

    );
}

export default AddDevelopers;